<template>
  <div class="member-row">
    <div class="member-info">
      <Avatar
        :personish="{
          kind: 'attendee',
          email: memberId,
          name: memberDetails?.name,
        }"
        size="small"
        badge-side="hide"
      />
      <div class="member-details">
        <div class="name">
          {{ memberDetails?.name || memberId.split("@")[0] }}
        </div>
        <div class="email">{{ memberId }}</div>
      </div>
    </div>
    <div class="member-actions">
      <span class="status-badge">{{ roleLabel }}</span>
      <ButtonMenu :offset="[0, 4]" placement="bottom-end">
        <template #btn>
          <BaseButton kind="auxiliary">
            <template #icon>
              <MoreIcon />
            </template>
          </BaseButton>
        </template>
        <template #content>
          <BaseButton
            kind="auxiliary"
            :text="setRoleText"
            is-expanded
            :state="$props.canSetRole ? 'default' : 'disabled'"
            :tooltip="$props.canSetRole ? undefined : $props.tooltip"
            :tooltip-display="$props.canSetRole ? 'none' : 'always'"
            @click="$emit('setRole', memberId, otherRole)"
          >
            <template #icon>
              <LockIcon />
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            text="Remove Member"
            is-expanded
            :state="$props.canRemove ? 'default' : 'disabled'"
            :tooltip="$props.canRemove ? undefined : $props.tooltip"
            :tooltip-display="$props.canRemove ? 'none' : 'always'"
            @click="$emit('remove', memberId)"
          >
            <template #icon>
              <DeleteIcon />
            </template>
          </BaseButton>
        </template>
      </ButtonMenu>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import { Member, WorkspaceAccessRole } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import Avatar from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import LockIcon from "@/components/icons/LockIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import * as inject from "@/components/inject";
import { releasableRef, useReleaser } from "@/ref";

export default defineComponent({
  name: "WorkspaceMemberRow",
  components: {
    Avatar,
    BaseButton,
    ButtonMenu,
    DeleteIcon,
    LockIcon,
    MoreIcon,
  },
  props: {
    memberId: { type: String, required: true },
    organizationId: { type: String, required: false },
    role: {
      type: String as PropType<WorkspaceAccessRole>,
      required: true,
    },
    canRemove: { type: Boolean, required: true },
    canSetRole: { type: Boolean, required: true },
    tooltip: { type: String, required: false },
  },
  emits: {
    remove: (_memberId: string) => true,
    setRole: (_memberId: string, _role: WorkspaceAccessRole) => true,
  },
  async setup(props) {
    const controller = inject.controller();
    const releaser = useReleaser();

    // Set up reactive member data with natural dependencies
    const memberDetailsPromise = releaser.add(
      (async () => {
        if (!props.organizationId) {
          return releasableRef<Member | null>(null);
        }
        const memberRef = await controller.organization.member.get(
          props.organizationId,
          props.memberId
        );
        const member = memberRef.value;
        memberRef.release();
        return releasableRef(member);
      })()
    );

    const roleLabel = computed(() =>
      props.role === "reader-writer" ? "Edit" : "Read"
    );

    const otherRole = computed<WorkspaceAccessRole>(() =>
      props.role === "reader-writer" ? "reader" : "reader-writer"
    );

    const setRoleText = computed(() =>
      props.role === "reader-writer"
        ? "Set permissions to Read"
        : "Set permissions to Edit"
    );

    const memberDetails = await memberDetailsPromise;

    return {
      memberDetails,
      roleLabel,
      otherRole,
      setRoleText,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss" as *;

.member-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-radius: $border-radius-m;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 12px;

  .member-details {
    .name {
      @extend %font-body-medium;
      @include themed() {
        color: t($primary);
      }
    }

    .email {
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }
}

.member-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  @include themed() {
    background-color: t($grey-10);
    color: t($grey-60);
  }
}
</style>
